import moment from 'moment';
import React, { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import { gitMetricColDef, defaultColDef } from "../../helpers/GitMetricColumnDef";
import { Col, Row } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import PrComments from "./pr-details/PrComments";
import CommitDetails from "./commit-details/CommitDetails"
import PrDetails from "./pr-details/PrDetails"
import ExportToExcelButton from "../common/ExportToExcelButton";
import { saveFolderMappings, getFolderOptions, storeFolderOption } from '../../services/apiServices';
import { useAnalyticsFilters } from "../../context/AnalyticsFilterContext";
import FolderFilter from "../common/FolderFilter";

const GitMetricsAgGrid = ({ startDate, endDate, gitMetricsGrid, platform }) => {
    const [homePageColDef, setHomePageColDef] = useState(gitMetricColDef);
    // const [folderOptions, setfolderOptions] = useState([]);
    const [newfolder, setNewfolder] = useState("");
    const [customColumnCount, setCustomColumnCount] = useState(1);
    const folderChangesRef = useRef(new Map());
    const saveTimeoutRef = useRef(null);
    const loggedInUserDetails = JSON.parse(localStorage.getItem('loggedInUserDetails')); 
    // const [selectedFolder, setSelectedFolder] = useState("All");
    const gridApiRef  = useRef(null)
    const { selectedFolder, folderOptions, setFolderOptions } = useAnalyticsFilters();


    const startEndDates = {
        startDate,
        endDate
    }
    const hasAddedColumn = useRef(false);

    useEffect(() => {
        if (
            platform === "AzureDevOps" &&
            !hasAddedColumn.current
        ) {
            const colDef = [...gitMetricColDef];
            colDef.splice(11, 0, {
                field: "changesCountModified",
                headerName: "# LOC: Modified",
                headerTooltip: "Number of Modified Lines",
                filter: "agNumberColumnFilter",
                width: 140,
                type: "numericColumn",
                aggFunc:'sum',
                suppressAggFuncInHeader: true,
                valueGetter: (params) => {
                    const total = Number(params.data?.changesTotal || 0);
                    const removed = Number(params.data?.changesCountRemoved || 0);
                    const added = Number(params.data?.changesCountAdd || 0);
                    return total - removed - added;
                }
            });

            colDef.splice(10, 0, {
                field: "SuspiciousLines",
                headerName: "# Likely Bulk Paste",
                headerTooltip: "",
                filter: "agNumberColumnFilter",
                width: 160,
                type: "numericColumn",
                aggFunc:'sum',
                suppressAggFuncInHeader: true,
                valueGetter: (params) => {
                    const adjustedLoc = Number(params.data?.suspectedLinesCount || 0);
                    return adjustedLoc;
                },
                cellRenderer: suspiciousLinesRenderer
            });

            colDef.splice(12, 0, {
                field: "Not commited SuspiciousLines",
                headerName: "# Not commited Suspicious Lines",
                headerTooltip: "",
                filter: "agNumberColumnFilter",
                width: 160,
                type: "numericColumn",
                aggFunc:'sum',
                suppressAggFuncInHeader: true,
                valueGetter: (params) => {
                    const adjustedLoc = Number(params.data?.notCommittedLinesCount || 0);
                    return adjustedLoc;
                },
                cellRenderer: notCommitedSuspiciousLinesRenderer
            });

            colDef.splice(13, 0, {
                field: "adjustedLOC",
                headerName: "# LOC: Total Adjusted",
                headerTooltip: "Adjusted LOC (added + modified - removed excluding deletions)",
                filter: "agNumberColumnFilter",
                width: 160,
                type: "numericColumn",
                suppressAggFuncInHeader: true,
                aggFunc:'sum',
                headerClass: "highlight-header",
                cellClass:"highlight-column",
                valueGetter: (params) => {
                    const adjustedLoc = Number(params.data?.adjustedChangesCountTotal || 0);
                    return adjustedLoc;
                },
                cellRenderer: adjustedLOCRenderer
            });

            colDef.splice(9, 0, {
                field: "workDonePerHour",
                headerName: "Productivity Factor",
                headerTooltip: "LOC: Total / Non-Calls Worked Hours",
                width: 160,
                type: "numericColumn",
                aggFunc:'sum',
                suppressAggFuncInHeader: true,
                valueGetter: (params) => {
                    const totalLOC = Number(params.data?.changesCountTotal || 0);
                    const checkinAgentHours = Number(params.data?.totalHoursWorked || 0);
                    const hrmsWorkHours = Number(params.data?.hrmsWorkHours || 0)
                    const totalHours = checkinAgentHours ? checkinAgentHours : hrmsWorkHours
                    return totalHours ? totalLOC / totalHours : 0;
                },
                valueFormatter: (params) => (params.value ? params.value.toFixed(2) : '0')
            });
            
            // Dynamic folder column
            colDef.unshift({
                field: "folder",
                headerName: "Folder",
                editable: true,
                cellEditor: "agSelectCellEditor",
                cellEditorParams: () => ({ values: folderOptions }),
                enableRowGroup: true,
                filter: "agSetColumnFilter",
                width: 120,
                valueGetter: (params) => params.data?.folder || "Default",
            });
            colDef.splice(2, 0,{
                field: "agentRunning",
                headerName: "Agent Running",
                headerTooltip: "Shows if the agent is running",
                width: 130,
                cellRenderer: agentRunningRenderer,
                filter: "agTextColumnFilter",
                cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' }
            });
            

            setHomePageColDef(colDef);
            hasAddedColumn.current = true;
        } else if (platform !== "AzureDevOps") {
            setHomePageColDef(gitMetricColDef);
            hasAddedColumn.current = false;
        }
    }, [platform]);

    // Refresh folder options dynamically
    useEffect(() => {
        setHomePageColDef(prevColDef =>
          prevColDef.map(col => {
            if (col.field === "folder" || col.field?.startsWith("custom_")) {
              return {
                ...col,
                cellEditor: "agSelectCellEditor",       
                cellEditorParams: { values: folderOptions }
              };
            }
            return col;
          })
        );
      }, [folderOptions]);

      //Refresh on folder selection
      useEffect(()=>{
        if(gridApiRef?.current){
            gridApiRef?.current?.onFilterChanged()
        }
      },[selectedFolder])

    // Assign other cell renderers
    homePageColDef.forEach(column => {
        if (column["field"] === "totalPrComments") {
            column["cellRenderer"] = prCommentRenderer
        }
        if (column["field"] === "totalCommits") {
            column["cellRenderer"] = totalCommitsRenderer
        }
        if (column["field"] === "totalMergedPrs" || column["field"] === "totalDeclinedPrs" || column["field"] === "totalOpenPrs") {
            column["cellRenderer"] = prDetailsRenderer
        }
        if (column["field"] === "changesCountRemoved") {
            column["cellRenderer"] = changesCountRemovedRenderer
        }
        if (column["field"] === "changesCountTotal") {
            column["cellRenderer"] = changesCountTotalRenderer
        }
        if (column["field"] === "changesCountModified") {
            column["cellRenderer"] = changesCountModifiedRenderer
        }
    });

    // Add custom column dynamically
    const addCustomColumn = () => {
        const newField = `custom_${customColumnCount + 1}`;
        const newCol = {
            field: newField,
            headerName: `Folder ${customColumnCount + 1}`,
            editable: true,
            cellEditor: "agSelectCellEditor",
            cellEditorParams: () => ({ values: folderOptions }), // same as Folder
            enableRowGroup: true,
            filter: "agSetColumnFilter",
            width: 150,
            valueGetter: (params) => params.data?.[newField] || "",
          };
          setHomePageColDef((prev) => {
            // always insert after the "folder" column (index 0)
            const colDef = [...prev];
            colDef.splice(1, 0, newCol);
            return colDef;
          });
        setCustomColumnCount(customColumnCount + 1);
    };

    const debouncedSaveFolders = () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        
        saveTimeoutRef.current = setTimeout(async () => {
            const payload = Array.from(folderChangesRef.current.values());
    
            if (!payload.length) return;
    
            try {
                await saveFolderMappings(payload, loggedInUserDetails);
                folderChangesRef.current.clear();
                console.log("Folder mappings saved:", payload);
            } catch (err) {
                console.error("Failed to save folder mappings", err);
            }
        }, 1500); // 1.5s debounce
    };    

    const onCellValueChanged = (params) => {
        if (params.colDef.field === "folder") {
            const allRows = [];
            params.api.forEachNode(node => {
                
                allRows.push({
                    developer: node.data.developer,
                    folder: node.data.folder || "None",
                    repoName:node.data.repoName,
                    platform:platform
                });
            });
    
            const {developer, repoName, folder } = params.data
            const key = `${developer + repoName + platform}`

            folderChangesRef.current.set(key, {
                developer,
                repoName,
                platform,
                folder: folder || "Default"
            });
        
            debouncedSaveFolders();

        }
    };
    
    const isExternalFilterPresent = () => {
        return selectedFolder !== "All"
    }

    const doesExternalFilterPass = (node) => {
        if (selectedFolder === "All") return true;
        return node?.data?.folder === selectedFolder
    }

    // ==================== RENDER ====================
    return (
        <div className="w-full">
            {/* folder Master List UI */}
            <Row className="mb-3">
            <Col className="d-flex justify-content-end">
                    <div className="d-flex gap-2 align-items-center">
                        {/* <select
                            className="form-select"
                            style={{ height: "38px", width: "160px" }}
                            value={selectedFolder}
                            onChange={(e) => setSelectedFolder(e.target.value)}
                        >
                            <option value="All">All</option>
                            {folderOptions.map(folder => (
                                <option key={folder} value={folder}>
                                    {folder}
                                </option>
                            ))}
                        </select> */}
                         <FolderFilter />
                <input
                    type="text"
                    value={newfolder}
                    placeholder="Add new folder"
                    onChange={(e) => setNewfolder(e.target.value)}
                    className="form-control"
                    style={{ height: "38px", width: "auto" }} // same as Bootstrap default btn height
                />
                <button
                    className="btn btn-primary"
                    style={{ height: "38px" }}
                    onClick={async() => {
                    if (newfolder && !folderOptions.includes(newfolder)) {
                        try{
                            setFolderOptions([...folderOptions, newfolder]);
                            await storeFolderOption(newfolder, loggedInUserDetails)
                            setNewfolder("");
                        }catch{
                            console.log("Failed to store ")
                        }                     
                    }
                    }}
                >
                    Add Folder
                </button>
                <button
                    className="btn btn-secondary"
                    style={{ height: "38px" }}
                    onClick={addCustomColumn}
                >
                    Add Column
                </button>
                <div style={{ height: "38px" }}>
                    <ExportToExcelButton
                        data={gitMetricsGrid}
                        filename="git-metrics-report"
                        sheetName="metrics"
                        headerColor="FFFFFF"
                        headerText={`Git Metrics For Date Range ${moment(startDate).format('ll')} to ${moment(endDate).format('ll')}`}
                    />
                </div>
                </div>
               
            </Col>
            </Row>
            <Row>
                <Col className={"ag-theme-quartz full-page-grid"}>
                    <AgGridReact
                        rowData={gitMetricsGrid}
                        columnDefs={homePageColDef}
                        defaultColDef={defaultColDef}
                        className="ag-grid-style"
                        rowGroupPanelShow="always"
                        onCellValueChanged={onCellValueChanged}
                        onGridReady={(params) => {
                            gridApiRef.current = params.api;
                        }}
                        suppressAggFuncInHeader={true}   

                        autoGroupColumnDef={{
                            suppressAggFuncInHeader: true
                        }}

                        isExternalFilterPresent={isExternalFilterPresent}
                        doesExternalFilterPass={doesExternalFilterPass}
                    />
                </Col>
            </Row>
        </div>
    )
}

GitMetricsAgGrid.propTypes = {
    gitMetricsGrid: PropTypes.array.isRequired,
    startDate: PropTypes.any.isRequired,
    endDate: PropTypes.any.isRequired,
    platform: PropTypes.string.isRequired
};

// Other renderers
function prCommentRenderer(params) {
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    if (params.value) return <PrComments usersMetrics={params.data} platform={params.data.platform} />;
}

function totalCommitsRenderer(params) {
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    if (params.value) {
        return <CommitDetails usersMetrics={params.data} platform={params.data.platform} startEndDates={{ startDate: params.startDate, endDate: params.endDate }} />;
    }
}

function prDetailsRenderer(params) {
    if (params?.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    if (params.value) {
        return <PrDetails usersMetrics={params.data} prType={params.colDef.field} platform={params.data.platform} startEndDates={{ startDate: params.startDate, endDate: params.endDate }} />;
    }
}

function adjustedLOCRenderer(params) {
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    if (params.value && params.value !== null) {
        let withoutDeleteRemoved = (params.data.changesCountTotal || 0) - (params.data.fileDeletedLinesChanges || 0);
        let suspectedLinesCount = (params.data.suspectedLinesCount || 0);
        let adjustedLoc = (withoutDeleteRemoved - suspectedLinesCount);
        return <div>{`${adjustedLoc}`}</div>;
    }
    return 0;
}

function suspiciousLinesRenderer(params) {
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    return <div>{Number(params.data?.suspectedLinesCount || 0)}</div>;
}

function notCommitedSuspiciousLinesRenderer(params) {
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    return <div>{Number(params.data?.notCommittedLinesCount || 0)}</div>;
}

function changesCountRemovedRenderer(params) {
   
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    let withoutDeleteRemoved = (params.data.changesCountRemoved || 0) - (params.data.fileDeletedLinesChanges || 0);
    if (!params.data.fileDeletedLinesChanges) return <div>{params.data.changesCountRemoved || ""}</div>;
    return <div>{`${withoutDeleteRemoved} (${params.data.fileDeletedLinesChanges})`}</div>;
}

function changesCountTotalRenderer(params) {
   
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    let withoutDeleteRemoved = (params.data.changesCountTotal || 0) - (params.data.fileDeletedLinesChanges || 0);
    return <div>{withoutDeleteRemoved || ""}</div>;
}

function changesCountModifiedRenderer(params) {
   
    if (params.node?.group) {
        return <div>{params.value}</div>;
    }
    if (!params.data) return null;
    let modifiedLOC = (params.data.changesTotal || 0) - (params.data.changesCountRemoved || 0) - (params.data.changesCountAdd || 0);
    return <div>{modifiedLOC || ""}</div>;
}

function agentRunningRenderer(params) {
    const { isRunning, agentUpdatedAt } = params.data || {};
    return (
        <div
            style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: isRunning ? "green" : "lightgray",
                margin: "0 auto",
            }}
            title={
                agentUpdatedAt
                    ? `${isRunning ? "Running" : "Stopped"} | Updated at: ${agentUpdatedAt.split("T")[0]}`
                    : isRunning
                    ? "Running"
                    : "Stopped"
            }
        />
    );
}


export default GitMetricsAgGrid;

